/**
 * Template call-context config and note selection — frontend mirror of
 * functions/helpers/callContext.js (keep both in sync; the parity test
 * compares them). The backend still builds the prompt; the frontend uses the
 * mirror to show which notes a template will pick up and to hold sends.
 */

export const CALL_CONTEXT_ACTIVITY_OPTIONS = [
  { id: "call", label: "Calls", icon: "fa-phone" },
  { id: "meeting", label: "Meetings", icon: "fa-handshake" },
  { id: "voicemail", label: "Voicemails", icon: "fa-voicemail" },
];

export const CALL_CONTEXT_SCOPE_OPTIONS = [
  { id: "since_last_email", label: "Since last email", desc: "Only calls logged after the last email to this contact" },
  { id: "latest", label: "Latest call", desc: "Only the most recent call, even if older than the last email" },
  { id: "recent", label: "All recent", desc: "Up to 5 calls within the time window" },
];

export const CALL_CONTEXT_USAGE_OPTIONS = [
  { id: "recap_next_step", label: "Recap + next step", icon: "fa-list-check", desc: "Pick up what was said, confirm the next step" },
  { id: "send_info", label: "Send requested info", icon: "fa-paper-plane", desc: "Deliver what the contact asked for" },
  { id: "objections", label: "Handle objections", icon: "fa-shield-halved", desc: "Answer the concerns they raised" },
  { id: "background", label: "Background only", icon: "fa-eye", desc: "Use it for relevance, don't mention the call" },
];

export const CALL_CONTEXT_MAX_AGE_OPTIONS = [7, 14, 30, 90];

const ACTIVITY_IDS = CALL_CONTEXT_ACTIVITY_OPTIONS.map((o) => o.id);
const SCOPE_IDS = CALL_CONTEXT_SCOPE_OPTIONS.map((o) => o.id);
const USAGE_IDS = CALL_CONTEXT_USAGE_OPTIONS.map((o) => o.id);

export const DEFAULT_CALL_CONTEXT = {
  enabled: false,
  scope: "since_last_email",
  maxAgeDays: 30,
  activities: [...ACTIVITY_IDS],
  usage: "recap_next_step",
  instructions: "",
  whenMissing: "generate",
};

export const normalizeCallContext = (raw) => {
  const c = raw && typeof raw === "object" ? raw : {};
  const picked = Array.isArray(c.activities)
    ? [...new Set(c.activities.filter((a) => ACTIVITY_IDS.includes(a)))]
    : [];
  const age = Number(c.maxAgeDays);
  return {
    enabled: c.enabled === true,
    scope: SCOPE_IDS.includes(c.scope) ? c.scope : DEFAULT_CALL_CONTEXT.scope,
    maxAgeDays: Number.isFinite(age) && age > 0 ? Math.min(Math.round(age), 365) : DEFAULT_CALL_CONTEXT.maxAgeDays,
    activities: picked.length ? picked : [...ACTIVITY_IDS],
    usage: USAGE_IDS.includes(c.usage) ? c.usage : DEFAULT_CALL_CONTEXT.usage,
    instructions: String(c.instructions || "").trim().slice(0, 600),
    whenMissing: c.whenMissing === "hold" ? "hold" : "generate",
  };
};

const DAY_MS = 86400000;
const MAX_NOTES = 5;

export const CALL_CONTEXT_HOLD_REASONS = {
  missing: "call_notes_missing",
  not_interested: "call_not_interested",
};

const toMillis = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "object" && typeof value.toMillis === "function") return value.toMillis();
  const ms = typeof value === "number" ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : 0;
};

const lastSendMs = (contact) => {
  if (!contact || typeof contact !== "object") return null;
  const sentAt = contact.lastEmailSent && contact.lastEmailSent.sentAt;
  if (typeof sentAt === "number" && Number.isFinite(sentAt)) return sentAt;
  if (typeof sentAt === "string" && sentAt.trim()) {
    const parsed = Date.parse(sentAt);
    if (Number.isFinite(parsed)) return parsed;
  }
  const leadTs = contact.lead_last_email && contact.lead_last_email.timestamp;
  if (typeof leadTs === "number" && Number.isFinite(leadTs)) return leadTs;
  if (typeof contact.lead_contacted_timestamp === "number" && Number.isFinite(contact.lead_contacted_timestamp)) {
    return contact.lead_contacted_timestamp;
  }
  return null;
};

export const selectCallNotes = (leadNotes, rawConfig, { lastEmailAt = null, now = Date.now() } = {}) => {
  const config = normalizeCallContext(rawConfig);
  const oldest = now - config.maxAgeDays * DAY_MS;
  const candidates = (Array.isArray(leadNotes) ? leadNotes : [])
    .filter((n) => n && typeof n === "object")
    .filter((n) => (n.type || "private") !== "outreach")
    .filter((n) => config.activities.includes(n.activity))
    .filter((n) => n.excludeFromAi !== true)
    .filter((n) => n.outcome !== "wrong_number")
    .filter((n) => String(n.text || "").trim() || n.outcome)
    .map((n) => ({ note: n, ms: toMillis(n.createdAt) }))
    .filter(({ ms }) => ms > 0 && ms >= oldest && ms <= now + DAY_MS)
    .sort((a, b) => b.ms - a.ms);

  let picked;
  if (config.scope === "latest") {
    picked = candidates.slice(0, 1);
  } else if (config.scope === "since_last_email" && lastEmailAt) {
    picked = candidates.filter(({ ms }) => ms > lastEmailAt).slice(0, MAX_NOTES);
  } else {
    picked = candidates.slice(0, MAX_NOTES);
  }
  return picked.map(({ note }) => note);
};

/** { status: 'off' | 'ok' | 'missing' | 'not_interested', config, notes } */
export const resolveCallContext = ({ contact, topic, now = Date.now() } = {}) => {
  const config = normalizeCallContext(topic && topic.callContext);
  if (!config.enabled) return { status: "off", config, notes: [] };
  const notes = selectCallNotes(contact && contact.lead_notes, config, {
    lastEmailAt: lastSendMs(contact),
    now,
  });
  if (notes.length === 0) return { status: "missing", config, notes };
  if (notes[0].outcome === "not_interested") return { status: "not_interested", config, notes };
  return { status: "ok", config, notes };
};

export const callContextHoldReason = (resolved) => {
  if (!resolved) return null;
  if (resolved.status === "not_interested") return CALL_CONTEXT_HOLD_REASONS.not_interested;
  if (resolved.status === "missing" && resolved.config && resolved.config.whenMissing === "hold") {
    return CALL_CONTEXT_HOLD_REASONS.missing;
  }
  return null;
};

export const callContextHoldMessage = (reason) => {
  if (reason === CALL_CONTEXT_HOLD_REASONS.not_interested) {
    return "Held: the last logged call says the contact is not interested";
  }
  if (reason === CALL_CONTEXT_HOLD_REASONS.missing) {
    return "Held: the template needs a call note and none matches its Call Context settings";
  }
  return "";
};
